import { useState, useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import TuneIcon from "@mui/icons-material/Tune";
import { AgGridReact } from "ag-grid-react";
import {
  Bar, BarChart, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import { BucketToggle } from "@plannel/components/dashboard/DashboardToggle";
import DashboardLayoutModal from "@plannel/components/dashboard/DashboardLayoutModal";
import ChartConfigModal from "@plannel/components/modal/ChartConfigModal";
import { KpiCard } from "@plannel/components/dashboard/KpiCard";
import SeriesLegend from "@plannel/components/dashboard/SeriesLegend";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import { DashboardComposedChart, barOrLine, areaGradientDefs, DW_CHART_TYPES } from "@plannel/components/dashboard/DashboardChart";
import { gridLine, axisTick, tooltipProps } from "@plannel/components/dashboard/chartTokens";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import { dashboardDefaultColDef, dashboardGridSx, DASHBOARD_ROW_HEIGHT } from "@plannel/components/dashboard/dashboardGrid";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import useDashboardP13n, { MAX_TOP_N } from "@plannel/hooks/useDashboardP13n";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import supplyPlanService from "@plannel/services/dashboard/supply-plan-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ZDate from "@plannel/utils/zdate";
import { FS } from "@plannel/utils/font-util";

/*
 * Supply Plan Status — 공급계획이 수요를 따라가는지(수급 밸런스) + 그 계획이 실행 가능한지(능력·제약)를 본다.
 * 소스/산식 명세: saas-application/docs/sql/dw/dashboard-spec/03_supply_plan.md
 * 데이터는 전부 DW 실조회다(목업 없음). 품목 계층 레벨 드롭다운만 백엔드 미구현이라 화면에 안 나온다.
 *
 * 금액은 주문(z_fact_mp_order) 축에만 존재하므로 이 화면엔 AMT/QTY 페이지 토글이 없다(수량 기본 + 부족만 금액 병기).
 */

// 카드 정의 — span(6열 기준)·행 높이. 기본 레이아웃 순서 = 이 배열 순서.
//   minH 는 행 트랙 minmax(minH px, minH fr) 로 쓰인다 → "최소 높이" 겸 "남는 세로 분배 가중치".
const CARD_DEFS = [
  { key: "balance", span: 4, minH: 300 },
  { key: "invProjection", span: 2, minH: 300 },
  { key: "capacity", span: 4, minH: 280 },
  { key: "constraint", span: 2, minH: 280 },
  { key: "shortage", span: 3, minH: 260 },
  { key: "exception", span: 3, minH: 260 },
  /*
    라인별 생산량은 **맨 뒤**에 둔다 — 앞에 끼우면 기존 3 행 배치(4+2 / 4+2 / 3+3)가 밀려
    이미 쓰고 있던 사람들의 화면이 통째로 재배열된다. 폭은 6(한 행 전체) — 막대가 길어야
    라인 간 차이를 눈으로 잴 수 있고, 마지막 행이라 옆에 짝지을 카드도 없다.
    (저장된 레이아웃에 없는 새 키는 useDashboardP13n 의 normalizeLayout 이 이 자리에 끼워 준다.)
  */
  { key: "lineProd", span: 6, minH: 220 },
];
const DEFAULT_LAYOUT = { order: CARD_DEFS.map((c) => c.key), hidden: {} };

// 시리즈별 차트 타입·표시 기본값(z_p13n config_obj.chart, 사용자별). 차트 3장 전부가 이 한 객체를 공유한다
// (z_p13n 은 `chart` 상위 키 하나에 저장되므로 시리즈 key 는 페이지 안에서 유일해야 한다).
//   공급/출하/부족은 막대, 비율·일수는 보조축이라 라인, 기말재고는 영역.
//   수요·안전재고·총능력은 "기준선" 성격이라 DASHED(점선) — 실적 시리즈와 눈으로 구분된다.
const DEFAULT_CHART_CONFIG = {
  // 수급 밸런스
  demand: { chartType: "DASHED", activeFlg: true },
  supply: { chartType: "BAR", activeFlg: true },
  shipment: { chartType: "BAR", activeFlg: true },
  shortage: { chartType: "BAR", activeFlg: true },
  fillRate: { chartType: "LINE", activeFlg: true },
  // 예상 재고
  eoh: { chartType: "AREA", activeFlg: true },
  safety: { chartType: "DASHED", activeFlg: true },
  dos: { chartType: "LINE", activeFlg: true },
  // 능력 부하 — 부하/가용/초과는 한 스택(BAR·AREA 일 때만 쌓인다)
  capaLoad: { chartType: "BAR", activeFlg: true },
  capaAvail: { chartType: "BAR", activeFlg: true },
  capaOver: { chartType: "BAR", activeFlg: true },
  capaTotal: { chartType: "DASHED", activeFlg: true },
  util: { chartType: "LINE", activeFlg: true },
};

// 시리즈 팔레트 인덱스 — 차트 색과 설정 모달 스와치가 공유한다(색 불일치 방지).
const BALANCE_COLOR_IDX = { demand: 0, supply: 2, shipment: 3, fillRate: 1 };
const INV_COLOR_IDX = { eoh: 0, dos: 1 };
const CAPACITY_COLOR_IDX = { capaLoad: 0, capaAvail: 4, capaTotal: 14, util: 1 };
const CAPA_STACK_ID = "cap";

/*
 * 드릴스루 대상 — TabMenuList 의 reduxKey(= 대상 페이지 viewName). 이 화면의 소스가 전부 MP fact 라
 * 대상도 전부 Master Plan 운영 화면이다. 대상 전부 거점 필터가 `MPLocationTreeFilter` 라
 * `filter.locations = [{ locCd }]` 한 모양으로 통한다.
 */
const DRILL = {
  planReview: "MP_PLAN_REVIEW",             // 4101 공급계획 검토
  psi: "MP_PSI_SIMULATION",                 // 4102 PSI 시뮬레이션
  exceptions: "MP_PRODUCTION_EXCEPTIONS",   // 4103 생산 예외 관리
  materialConst: "MATERIAL_CONSTRAINTS",    // 4001 자재 제약
  mpDemand: "MP_DEMAND",                    // 4002 공급계획 수요
  resourceUtil: "RESOURCE_UTILIZATION",     // 4203 자원 부하 분석
  orderTracking: "ORDER_TRACKING",          // 4204 주문 추적
};

// ─────────────────────────────────────────
// ① 수급 밸런스 추이 — 수요/공급/출하/부족 + 충족률(우측 %축)
// ─────────────────────────────────────────
const BalanceCard = ({ title, data, bucket, weekAvailable, onChangeBucket, chartConfig, onOpenConfig, onToggleSeries, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fillRateLabel = t("fillRate");

  // 색은 렌더 시점 계산 — 모듈 상수로 굳히면 사용자 팔레트 로드 전 값으로 고정된다.
  const SERIES = useMemo(() => ({
    demand: ColorUtil.colorSet(BALANCE_COLOR_IDX.demand),
    supply: ColorUtil.colorSet(BALANCE_COLOR_IDX.supply),
    shipment: ColorUtil.colorSet(BALANCE_COLOR_IDX.shipment),
    shortage: theme.palette.error.main,      // 부족은 의미색(위험)
    fillRate: ColorUtil.colorSet(BALANCE_COLOR_IDX.fillRate),
  }), [theme]);

  /*
   * 주(week) 뷰에서는 주문 측 2개 시리즈를 뺀다.
   * z_fact_mp_order 는 base_date 가 **due 월**이라 주 값이 없다(dashboard-spec §2-①).
   * Sales Status 가 주 뷰에서 전년동기·성장률을 빼는 것과 같은 처리.
   */
  const isWeek = bucket === "week";

  const legend = useMemo(() => {
    const items = [
      { key: "demand", label: t("demandQty"), color: SERIES.demand },
      { key: "supply", label: t("supplyQty"), color: SERIES.supply },
      { key: "shipment", label: t("shipmentQty"), color: SERIES.shipment },
    ];
    if (isWeek) return items;
    return [
      ...items,
      { key: "shortage", label: t("shortQty"), color: SERIES.shortage },
      { key: "fillRate", label: `${fillRateLabel} (%)`, color: SERIES.fillRate },
    ];
  }, [t, fillRateLabel, SERIES, isWeek]);

  // 시리즈 표시 = 설정 activeFlg. 범례 체크박스와 설정 모달이 같은 소스를 공유해 자동 동기화.
  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;

  return (
    <>
      <DashboardCardHeader title={title} onOpenConfig={onOpenConfig} {...drill}>
        <BucketToggle bucket={bucket} weekAvailable={weekAvailable} onChange={onChangeBucket} />
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data}
        yAxes={[
          { id: "qty", tickFormatter: fmtQty },
          // 주 뷰엔 충족률 시리즈가 없으므로 % 축도 그리지 않는다
          ...(isWeek ? [] : [{ id: "pct", orientation: "right", tickFormatter: (v) => `${v}%` }]),
        ]}
        tooltipFormatter={(v, name) => (name === `${fillRateLabel} (%)` ? `${v}%` : fmtQty(v))}
      >
        {areaGradientDefs([
          { dataKey: "demand", color: SERIES.demand },
          { dataKey: "supply", color: SERIES.supply },
          { dataKey: "shipment", color: SERIES.shipment },
          { dataKey: "shortage", color: SERIES.shortage },
          { dataKey: "fillRate", color: SERIES.fillRate },
        ])}
        {isActive("supply") && barOrLine(chartConfig, { key: "supply", dataKey: "supply", name: t("supplyQty"), color: SERIES.supply, yAxisId: "qty", dflt: "BAR" })}
        {isActive("shipment") && barOrLine(chartConfig, { key: "shipment", dataKey: "shipment", name: t("shipmentQty"), color: SERIES.shipment, yAxisId: "qty", dflt: "BAR" })}
        {!isWeek && isActive("shortage") && barOrLine(chartConfig, { key: "shortage", dataKey: "shortage", name: t("shortQty"), color: SERIES.shortage, yAxisId: "qty", dflt: "BAR" })}
        {isActive("demand") && barOrLine(chartConfig, { key: "demand", dataKey: "demand", name: t("demandQty"), color: SERIES.demand, yAxisId: "qty", dflt: "DASHED" })}
        {!isWeek && isActive("fillRate") && barOrLine(chartConfig, { key: "fillRate", dataKey: "fillRate", name: `${fillRateLabel} (%)`, color: SERIES.fillRate, yAxisId: "pct", dflt: "LINE" })}
      </DashboardComposedChart>
      <SeriesLegend items={legend} isActive={isActive} onToggle={onToggleSeries} />
    </>
  );
};

// ─────────────────────────────────────────
// ② 예상 재고 추이 — 기말재고 + 안전재고 기준선 + 재고일수(우측 축)
// ─────────────────────────────────────────
const InvProjectionCard = ({ title, data, bucket, weekAvailable, onChangeBucket, chartConfig, onOpenConfig, onToggleSeries, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const daysUnit = t("invDaysUnit");

  const SERIES = useMemo(() => ({
    eoh: ColorUtil.colorSet(INV_COLOR_IDX.eoh),
    safety: theme.palette.warning.main,      // 하한선은 의미색
    dos: ColorUtil.colorSet(INV_COLOR_IDX.dos),
  }), [theme]);

  /*
   * 안전재고는 데이터가 있을 때만 그린다.
   * IP 와 MP 가 서로 다른 품목군을 계획하면 비교 대상이 없어 백엔드가 이 시리즈를 아예 안 내려준다.
   * 그때 0 으로 그리면 "안전재고가 0" 으로 읽히므로 시리즈·범례에서 통째로 뺀다.
   */
  const hasSafety = data.some((p) => p.safety != null);

  // 표시 여부 = 설정 activeFlg(z_p13n) — 범례 체크박스와 설정 모달이 같은 소스를 공유한다.
  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;
  const dosLabel = t("daysOfSupply");

  return (
    <>
      <DashboardCardHeader title={title} onOpenConfig={onOpenConfig} {...drill}>
        <BucketToggle bucket={bucket} weekAvailable={weekAvailable} onChange={onChangeBucket} />
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data}
        yAxes={[
          { id: "qty", tickFormatter: fmtQty },
          { id: "days", orientation: "right", tickFormatter: (v) => `${Math.round(v)}` },
        ]}
        tooltipFormatter={(v, name) => (name === dosLabel ? `${v}${daysUnit}` : fmtQty(v))}
      >
        {/* AREA 로 바꿀 수 있는 시리즈는 전부 등록 — 빠지면 면이 검게 칠해진다 */}
        {areaGradientDefs([
          { dataKey: "eoh", color: SERIES.eoh },
          { dataKey: "safety", color: SERIES.safety },
          { dataKey: "dos", color: SERIES.dos },
        ])}
        {isActive("eoh") && barOrLine(chartConfig, { key: "eoh", dataKey: "eoh", name: t("eoh"), color: SERIES.eoh, yAxisId: "qty", dflt: "AREA" })}
        {hasSafety && isActive("safety") && barOrLine(chartConfig, { key: "safety", dataKey: "safety", name: t("safetyStock"), color: SERIES.safety, yAxisId: "qty", dflt: "DASHED" })}
        {isActive("dos") && barOrLine(chartConfig, { key: "dos", dataKey: "dos", name: dosLabel, color: SERIES.dos, yAxisId: "days", dflt: "LINE" })}
      </DashboardComposedChart>
      <SeriesLegend
        items={[
          { key: "eoh", label: t("eoh"), color: SERIES.eoh },
          ...(hasSafety ? [{ key: "safety", label: t("safetyStock"), color: SERIES.safety }] : []),
          { key: "dos", label: dosLabel, color: SERIES.dos },
        ]}
        isActive={isActive}
        onToggle={onToggleSeries}
      />
    </>
  );
};

// ─────────────────────────────────────────
// ③ 능력 부하 현황 — 부하+가용+초과 단일 스택(합 = 총 능력) + 총능력 라인 + 가동률(우측 %축)
// ─────────────────────────────────────────
const CapacityCard = ({ title, data, bucket, weekAvailable, onChangeBucket, resources, resourceCd, onChangeResource,
  chartConfig, onOpenConfig, onToggleSeries, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const utilLabel = t("capaUtil");

  const SERIES = useMemo(() => ({
    capaLoad: ColorUtil.colorSet(CAPACITY_COLOR_IDX.capaLoad),
    capaAvail: ColorUtil.colorSet(CAPACITY_COLOR_IDX.capaAvail),
    capaOver: theme.palette.error.main,
    capaTotal: ColorUtil.colorSet(CAPACITY_COLOR_IDX.capaTotal),
    util: ColorUtil.colorSet(CAPACITY_COLOR_IDX.util),
  }), [theme]);

  const resourceOptions = [{ value: "", label: t("spAllResource") }, ...resources];

  // 표시 여부 = 설정 activeFlg(z_p13n) — 범례 체크박스와 설정 모달이 같은 소스를 공유한다.
  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;

  return (
    <>
      <DashboardCardHeader title={title} onOpenConfig={onOpenConfig} {...drill}>
        <CompactSelect displayEmpty value={resourceCd} options={resourceOptions} onChange={onChangeResource} minWidth={128} />
        <BucketToggle bucket={bucket} weekAvailable={weekAvailable} onChange={onChangeBucket} />
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data}
        yAxes={[
          { id: "qty", tickFormatter: fmtQty },
          { id: "pct", orientation: "right", tickFormatter: (v) => `${v}%` },
        ]}
        tooltipFormatter={(v, name) => (name === utilLabel ? `${v}%` : fmtQty(v))}
      >
        {areaGradientDefs([
          { dataKey: "capaLoad", color: SERIES.capaLoad },
          { dataKey: "capaAvail", color: SERIES.capaAvail },
          { dataKey: "capaOver", color: SERIES.capaOver },
          { dataKey: "capaTotal", color: SERIES.capaTotal },
          { dataKey: "util", color: SERIES.util },
        ])}
        {/* 부하/가용/초과는 같은 stackId — BAR·AREA 일 때만 쌓이고, LINE 으로 바꾼 시리즈만 스택에서 빠진다 */}
        {isActive("capaLoad") && barOrLine(chartConfig, { key: "capaLoad", dataKey: "capaLoad", name: t("capaLoad"), color: SERIES.capaLoad, yAxisId: "qty", dflt: "BAR", stackId: CAPA_STACK_ID, barSize: 18 })}
        {isActive("capaAvail") && barOrLine(chartConfig, { key: "capaAvail", dataKey: "capaAvail", name: t("capaAvail"), color: SERIES.capaAvail, yAxisId: "qty", dflt: "BAR", stackId: CAPA_STACK_ID, barSize: 18 })}
        {isActive("capaOver") && barOrLine(chartConfig, { key: "capaOver", dataKey: "capaOver", name: t("capaOver"), color: SERIES.capaOver, yAxisId: "qty", dflt: "BAR", stackId: CAPA_STACK_ID, barSize: 18 })}
        {isActive("capaTotal") && barOrLine(chartConfig, { key: "capaTotal", dataKey: "capaTotal", name: t("capaTotal"), color: SERIES.capaTotal, yAxisId: "qty", dflt: "DASHED" })}
        {isActive("util") && barOrLine(chartConfig, { key: "util", dataKey: "util", name: utilLabel, color: SERIES.util, yAxisId: "pct", dflt: "LINE" })}
      </DashboardComposedChart>
      <SeriesLegend
        items={[
          { key: "capaLoad", label: t("capaLoad"), color: SERIES.capaLoad },
          { key: "capaAvail", label: t("capaAvail"), color: SERIES.capaAvail },
          { key: "capaOver", label: t("capaOver"), color: SERIES.capaOver },
          { key: "capaTotal", label: t("capaTotal"), color: SERIES.capaTotal },
          { key: "util", label: utilLabel, color: SERIES.util },
        ]}
        isActive={isActive}
        onToggle={onToggleSeries}
      />
    </>
  );
};

// ─────────────────────────────────────────
// ④ 제약 원인 분해 — 자재/능력/기타 도넛
// ─────────────────────────────────────────
// 원인 코드 → i18n 키. 백엔드는 0건 원인을 빼고 주므로 여기 없는 key 는 그대로 표시된다.
//   ★ data = 마스터 데이터 부족으로 계획을 못 만든 주문(mj 실측상 가장 큰 원인).
const CONSTRAINT_LABEL_KEY = { material: "materialConst", capacity: "capacity", data: "masterData", other: "others" };

const ConstraintCard = ({ title, data, drill, onSegmentDrill }) => {
  const { t } = useTranslation();
  const total = data.reduce((s, d) => s + (d.count ?? 0), 0);
  const rows = data.map((d, i) => ({
    ...d,
    label: t(CONSTRAINT_LABEL_KEY[d.key] ?? d.key),
    color: ColorUtil.colorSet(i * 2),
    pct: total > 0 ? Math.round((d.count / total) * 1000) / 10 : 0,
  }));

  /*
   * 원인마다 고칠 화면이 다르다(자재 제약 / 자원 부하 / 공급계획 수요) → 조각·범례를 각각 링크로 만든다.
   * 제목 링크(drill)는 원인 구분 없는 기본 대상(생산 예외 관리)이다.
   */
  const segLinkSx = onSegmentDrill ? {
    cursor: "pointer", borderRadius: 1, px: 0.4, mx: -0.4,
    transition: "background-color 0.15s",
    "&:hover": { bgcolor: "action.hover" },
  } : null;
  // recharts 조각 클릭 payload 는 버전에 따라 데이터가 한 겹 더 들어간다 — 양쪽에서 key 를 찾는다.
  const segKey = (d) => d?.key ?? d?.payload?.key ?? d?.payload?.payload?.key;

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} cx="50%" cy="50%" innerRadius="52%" outerRadius="80%" dataKey="count" nameKey="label"
              stroke="none" isAnimationActive={false}
              onClick={onSegmentDrill ? (d) => onSegmentDrill(segKey(d)) : undefined}>
              {/* 커서는 Cell(=sector path)에 준다 — Pie 래퍼에 준 style 은 sector 까지 안 내려간다 */}
              {rows.map((d) => (
                <Cell key={d.key} fill={d.color} style={onSegmentDrill ? { cursor: "pointer" } : undefined} />
              ))}
            </Pie>
            <Tooltip {...tooltipProps()} formatter={(v, n) => [`${v}`, n]} content={<AgStyleTooltipContent />} />
          </PieChart>
        </ResponsiveContainer>
        {/* 도넛 중앙 총계 */}
        <Box sx={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          textAlign: "center", pointerEvents: "none",
        }}>
          <Typography sx={{ fontSize: FS.KPI, fontWeight: 700, color: "text.primary", lineHeight: 1.1 }}>{total}</Typography>
          <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{t("count")}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 0.5, flexWrap: "wrap", flexShrink: 0 }}>
        {rows.map((d) => (
          <Box key={d.key}
            onClick={onSegmentDrill ? () => onSegmentDrill(d.key) : undefined}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, ...segLinkSx }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.4, bgcolor: d.color }} />
            <Typography sx={{ fontSize: FS.BODY, color: "text.primary" }}>{`${d.label} ${d.pct}%`}</Typography>
          </Box>
        ))}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 라인별 생산량 — 같은 계획 안에서 어느 라인이 얼마나 만드는가
// ─────────────────────────────────────────
/*
 * 능력 카드(③)는 자원을 **하나 골라서** 보지만 이 카드는 라인끼리 **나란히** 놓는다 — 고르는 것과
 *   비교하는 것은 다른 질문이고, 치우침은 나란히 놓아야만 보인다.
 *
 * 공장(workcenter) 으로 묶는 이유는 **비교 단위가 둘이기 때문**이다 — 같은 공장 안 라인끼리가
 *   1 차 비교(설비·인력이 같은 조건), 공장 합계끼리가 2 차 비교다. 묶음 합계를 머리줄에 둬서
 *   둘을 한 카드에서 읽는다.
 *   mj 실측(최근 12 개월)에서는 **라인 간 차이가 공장 간 차이보다 크다** — 공장은 163,570 vs
 *   148,151(1.10 배)인데 라인은 83,005 vs 70,736(1.17 배)이다. 즉 치우침은 공장 배분이 아니라
 *   라인 운영 쪽에 있다. 묶음이 없으면 어느 쪽 이야기인지 가릴 수 없다.
 *
 * 막대 길이는 **카드 안 최대값 기준**이다(합계 기준이 아니라) — 라인이 둘뿐인 테넌트에서 합계
 *   기준으로 그리면 막대가 전부 절반 아래에 깔려 서로 구분이 안 된다. 비중은 숫자로 따로 보여준다.
 */
// 라벨이 전부 데이터값(공장 코드·라인명)이라 이 카드에는 번역할 문구가 없다 — 제목만 호출부가 넘긴다.
const LineProdCard = ({ title, rows, drill }) => {
  const theme = useTheme();

  const groups = useMemo(() => {
    const max = Math.max(...(rows ?? []).map((r) => r.prodQty ?? 0), 0);
    const byWc = new Map();
    (rows ?? []).forEach((r) => {
      if (!byWc.has(r.wcCd)) byWc.set(r.wcCd, { wcCd: r.wcCd, lines: [], total: 0 });
      const g = byWc.get(r.wcCd);
      g.lines.push(r);
      g.total += r.prodQty ?? 0;
    });
    return { max, list: [...byWc.values()] };
  }, [rows]);

  if (!rows?.length) return (<><DashboardCardHeader title={title} dense {...drill} /></>);

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill} />
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 1.2, pt: 0.5 }}>
        {groups.list.map((g, gi) => (
          <Box key={g.wcCd}>
            {/* 공장 머리줄 — 묶음의 합계를 같이 둬야 "공장끼리" 비교가 된다 */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.4 }}>
              <Typography sx={{ fontSize: FS.CAPTION, fontWeight: 700, color: "text.primary" }}>
                {g.wcCd}
              </Typography>
              <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
                {fmtQty(g.total)}
              </Typography>
            </Box>
            {g.lines.map((r) => {
              const pct = groups.max > 0 ? ((r.prodQty ?? 0) / groups.max) * 100 : 0;
              return (
                <Box key={r.resourceCd} sx={{ mb: 0.6 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.2 }}>
                    <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
                      {r.resourceName || r.resourceCd}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                      <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: "text.primary" }}>
                        {fmtQty(r.prodQty)}
                      </Typography>
                      <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", minWidth: 42, textAlign: "right" }}>
                        {r.sharePct == null ? "-" : `${r.sharePct}%`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ height: 8, borderRadius: 1, bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
                    <Box sx={{
                      width: `${pct}%`, height: "100%", borderRadius: 1,
                      bgcolor: ColorUtil.colorSet(gi * 2),
                    }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// ⑤ 부족 상위 N — AG Grid (Top N 은 z_p13n 개인화)
// ─────────────────────────────────────────
/*
 * 품목 축 컬럼 — 이 화면에서 **품목 계층 레벨이 실제로 도는 유일한 자리**다(나머지 카드는 전 품목 합).
 *   최하위 레벨 = 품목 상세(코드·명), 상위 레벨 = 계층 노드 한 컬럼(서버가 접어서 준다).
 *   헤더는 선택한 레벨 라벨(z_hrchy_config.name — 테넌트 config 값이라 번역 대상이 아니다).
 */
const ShortageGrid = ({ title, rows, currencyCd, topN, onChangeTopN, drill, onRowDrill, grouped, groupLabel }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);

  const columnDefs = useMemo(() => [
    { headerName: t("location"), field: "loc", flex: 1, minWidth: 96 },
    ...(grouped
      ? [{ headerName: groupLabel, field: "itemGrp", flex: 1, minWidth: 140 }]
      : [
        { headerName: t("itemCd"), field: "itemCd", width: 88 },
        { headerName: t("itemName"), field: "itemNm", flex: 1, minWidth: 108 },
      ]),
    { headerName: t("demandQty"), field: "demandQty", width: 84, type: "rightAligned",
      valueFormatter: (p) => (p.value ?? 0).toLocaleString() },
    { headerName: t("supplyQty"), field: "supplyQty", width: 84, type: "rightAligned",
      valueFormatter: (p) => (p.value ?? 0).toLocaleString() },
    { headerName: t("shortQty"), field: "shortQty", width: 84, type: "rightAligned",
      valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
      cellStyle: {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        color: theme.palette.error.dark, fontWeight: 700, textAlign: "right",
      } },
    { headerName: t("spShortAmt"), field: "shortAmt", width: 92, type: "rightAligned",
      valueFormatter: (p) => fmtMoney(p.value ?? 0) },
    { headerName: t("fillRatePer"), field: "fillRate", width: 78, type: "rightAligned",
      valueFormatter: (p) => `${p.value ?? 0}%` },
  ], [t, theme, fmtMoney, grouped, groupLabel]);

  const topNOptions = Array.from({ length: MAX_TOP_N }, (_, i) => ({ value: i + 1, label: `Top ${i + 1}` }));

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>
        <CompactSelect value={topN} options={topNOptions} onChange={onChangeTopN} minWidth={78} />
      </DashboardCardHeader>
      <Box className="ag-theme-balham" sx={dashboardGridSx(theme)}>
        <AgGridReact
          theme="legacy"
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={dashboardDefaultColDef}
          rowHeight={DASHBOARD_ROW_HEIGHT}
          headerHeight={32}
          suppressCellFocus
          rowStyle={onRowDrill ? { cursor: "pointer" } : undefined}
          onRowClicked={onRowDrill ? (e) => onRowDrill(e.data) : undefined}
        />
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// ⑥ 예외/알럿 현황 — 유형별 건수 가로 막대(색 = 위험도)
// ─────────────────────────────────────────
const EXCEPTION_LABEL_KEY = { late: "spExcLate", short: "spExcShort", overload: "spExcOverload", safety: "spExcSafety" };

const ExceptionCard = ({ title, rows, drill }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // 위험도 → 의미색. 등급 정의는 fact 의 risk_level(CRITICAL/HIGH/MEDIUM/LOW).
  const riskColor = (risk) => ({
    CRITICAL: theme.palette.error.main,
    HIGH: theme.palette.error.light,
    MEDIUM: theme.palette.warning.main,
  }[risk] ?? theme.palette.primary.main);

  const data = rows.map((r) => ({
    ...r,
    label: t(EXCEPTION_LABEL_KEY[r.key] ?? r.key),
    color: riskColor(r.risk),
  }));
  const total = data.reduce((s, d) => s + (d.count ?? 0), 0);

  return (
    <>
      <DashboardCardHeader title={title} dense {...drill}>
        <Typography sx={{ fontSize: FS.BODY, color: "text.secondary" }}>{`${t("total")} ${total}`}</Typography>
      </DashboardCardHeader>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 12, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 4" stroke={gridLine(theme)} horizontal={false} />
            <XAxis type="number" tick={axisTick(theme)} allowDecimals={false} />
            <YAxis dataKey="label" type="category" tick={axisTick(theme)} width={92} />
            <Tooltip {...tooltipProps()} content={<AgStyleTooltipContent />} />
            <Bar dataKey="count" name={t("count")} radius={[0, 3, 3, 0]} barSize={18} isAnimationActive={false}>
              {data.map((d) => <Cell key={d.key} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const SupplyPlanDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();
  const theme = useTheme();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [locId, setLocId] = useState("");                                            // 거점 필터(전체 카드 재조회) — 기본은 항상 전체
  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevel ?? "");       // 품목 계층 집계 레벨
  const [resourceCd, setResourceCd] = useState("");                                  // 능력 카드 자원 필터
  const [balanceBucket, setBalanceBucket] = useState(reduxViewState?.balanceBucket ?? "month");
  const [invBucket, setInvBucket] = useState(reduxViewState?.invBucket ?? "month");
  const [capacityBucket, setCapacityBucket] = useState(reduxViewState?.capacityBucket ?? "month");
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [configScope, setConfigScope] = useState(null);   // 톱니를 누른 카드 key — 모달은 그 카드의 measure 만 보여준다
  const [drillHoverKey, setDrillHoverKey] = useState(null); // 제목 링크 hover 중인 카드 — 그 카드에만 하이라이트

  // 버킷은 재조회 불필요(월·주 모두 반환) — 집계/필터 파라미터만 재조회 트리거
  const { payload } = useDashboardData(supplyPlanService.get, {
    locId: locId || undefined,
    itemLevel: itemLevel || undefined,
    resourceCd: resourceCd || undefined,
  });

  // 사용자별 설정(z_p13n, pageCd = viewName) — 카드 레이아웃 + 밸런스 차트 타입 + 부족 Top N
  const { chartConfig, topN, layout, saveChartConfig, saveTopN, saveLayout, toggleSeries } =
    useDashboardP13n(viewName, { defaultChartConfig: DEFAULT_CHART_CONFIG, defaultLayout: DEFAULT_LAYOUT });

  useEffect(() => {
    // 버킷·레벨만 저장 — 거점/자원은 저장 안 함(새로 열면 항상 전체)
    const next = reduxUtil.createObjectState(viewName, { itemLevel, balanceBucket, invBucket, capacityBucket });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemLevel, balanceBucket, invBucket, capacityBucket]);

  const fmtMoney = useMemo(() => makeFmtMoney(payload?.currencyCd), [payload]);

  const cardLabels = useMemo(() => ({
    balance: t("spBalanceCard"),
    invProjection: t("spInvProjectionCard"),
    capacity: t("spCapacityCard"),
    constraint: t("spConstraintCard"),
    shortage: t("spShortageCard"),
    exception: t("spExceptionCard"),
    lineProd: t("spLineProdCard"),
  }), [t]);

  // 설정 모달 measure — **카드 스코프**(톱니를 누른 카드의 시리즈만). 색은 차트와 같은 소스라 스와치가 어긋나지 않는다.
  const configMeasures = useMemo(() => ({
    balance: [
      { key: "demand", label: t("demandQty"), hasActive: true, color: ColorUtil.colorSet(BALANCE_COLOR_IDX.demand) },
      { key: "supply", label: t("supplyQty"), hasActive: true, color: ColorUtil.colorSet(BALANCE_COLOR_IDX.supply) },
      { key: "shipment", label: t("shipmentQty"), hasActive: true, color: ColorUtil.colorSet(BALANCE_COLOR_IDX.shipment) },
      // 주문 측 2개는 월 전용 — 주 뷰에선 차트에 안 그려지므로 설정 목록에서도 뺀다
      { key: "shortage", label: t("shortQty"), hasActive: true, color: theme.palette.error.main, monthOnly: true },
      { key: "fillRate", label: t("fillRate"), hasActive: true, color: ColorUtil.colorSet(BALANCE_COLOR_IDX.fillRate), monthOnly: true },
    ],
    invProjection: [
      { key: "eoh", label: t("eoh"), hasActive: true, color: ColorUtil.colorSet(INV_COLOR_IDX.eoh) },
      { key: "safety", label: t("safetyStock"), hasActive: true, color: theme.palette.warning.main },
      { key: "dos", label: t("daysOfSupply"), hasActive: true, color: ColorUtil.colorSet(INV_COLOR_IDX.dos) },
    ],
    capacity: [
      { key: "capaLoad", label: t("capaLoad"), hasActive: true, color: ColorUtil.colorSet(CAPACITY_COLOR_IDX.capaLoad) },
      { key: "capaAvail", label: t("capaAvail"), hasActive: true, color: ColorUtil.colorSet(CAPACITY_COLOR_IDX.capaAvail) },
      { key: "capaOver", label: t("capaOver"), hasActive: true, color: theme.palette.error.main },
      { key: "capaTotal", label: t("capaTotal"), hasActive: true, color: ColorUtil.colorSet(CAPACITY_COLOR_IDX.capaTotal) },
      { key: "util", label: t("capaUtil"), hasActive: true, color: ColorUtil.colorSet(CAPACITY_COLOR_IDX.util) },
    ],
  }[configScope] ?? []).filter((r) => !(r.monthOnly && balanceBucket === "week")),
  [t, theme, configScope, balanceBucket]);

  // 부족 Top N 슬라이스 — 서버는 상위 10까지 주고 프런트가 자른다(재조회 없이 전환)
  const shownShortage = useMemo(() => (payload?.shortageRows ?? []).slice(0, topN), [payload, topN]);

  if (!payload) return null;

  const kpis = payload.kpis ?? {};
  const weekAvailable = (payload.availableBuckets ?? ["month"]).includes("week");
  const daysUnit = t("invDaysUnit");

  // 거점 셀렉터 옵션 — 맨 앞에 전체("")
  const locOptions = [{ value: "", label: t("allLocationLabel") }, ...(payload.locations ?? [])];
  /*
   * 품목 계층 레벨 셀렉터 — 레벨이 2개 이상일 때만 드롭다운이 나온다(레벨이 하나면 고를 게 없다).
   * 롤업이 실제로 도는 카드는 **부족 상위** 하나뿐이다 — 나머지는 전 품목 합이라 레벨과 무관하다.
   */
  const levelOptions = (payload.itemLevels ?? []).map((lv) => ({
    value: String(lv.value),
    label: lv.label || lv.cd || `${t("level")} ${lv.value}`,
  }));
  // 그룹 컬럼 헤더 = 선택 레벨 라벨. 서버가 해석한 itemLevel 을 기준으로 찾는다(요청값이 아니라).
  const activeLevel = levelOptions.find((lv) => lv.value === String(payload.itemLevel ?? ""));
  const itemGroupLabel = activeLevel?.label || t("itemLevel");

  /*
   * 드릴스루 (skill §9) — 대상 뷰의 상태를 심고 `updateHistoryState` 로 탭을 연다.
   * 버전은 **안 심는다**: 이 화면은 DW rolling 이라 MP 버전 개념이 없고, 안 넘기면 대상이 최신 버전을 자동 선택한다.
   */
  const drillLocations = () => {
    // 상단 거점 필터 → 대상의 filter.locations. 전체("")면 null = 필터 없음.
    //   MPLocationTreeFilter 는 locCd 로 원본 객체를 다시 찾아 표시·트리 체크를 스스로 복원한다.
    const sel = (payload.locations ?? []).find((l) => String(l.value) === String(locId));
    return sel ? [{ locCd: sel.cd, name: sel.label }] : null;
  };
  const drillPeriod = () => {
    // payload.period 는 "YYYY-MM" → 시작=1일 0시, 끝=말일 23:59:59
    const [fy, fm] = (payload.period?.from ?? "").split("-").map(Number);
    const [ty, tm] = (payload.period?.to ?? "").split("-").map(Number);
    return { start: new ZDate(fy, fm - 1, 1, 0, 0, 0), end: new ZDate(ty, tm, 0, 23, 59, 59) };
  };
  const drillTo = (target, state) => {
    reduxDispatch(reduxUtil.removeViewState({ viewName: target }));   // 이전 필터 제거(주입값 확실 적용)
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(target, state)));
    reduxDispatch(reduxUtil.updateHistoryState(target));              // 탭 전환
  };

  // ① 수급 밸런스 → 공급계획 검토(수요·생산·출하 계획 결과 상세)
  const openPlanReview = () => drillTo(DRILL.planReview, {
    filter: { locations: drillLocations(), category: null },
  });

  /*
   * ② 예상 재고 / ⑤ 부족 행 클릭 → PSI 시뮬레이션.
   * Resource Gantt → PSI 와 같은 규약: 이미 열린 탭은 재마운트되지 않아 viewState 를 다시 읽지 않으므로
   * 이벤트로도 알린다. viewState 는 **지우지 않고 덮어쓴다** — 컬럼 토글 같은 사용자 설정까지 날릴 이유가 없고,
   * 조회에 영향을 주는 키는 아래에서 전부 명시한다.
   */
  const openPsi = (itemCd) => {
    const psiCond = {
      currentPage: 1,
      filter: { locations: drillLocations(), category: null },
      itemSearch: itemCd ?? "",
      extraItemCodes: [],
      itemTypeSearch: null,           // 명시적 null = 품목 타입 필터 없음(FG 기본에 SFG 가 가려지지 않게)
      subGridResourceListFilter: [],
      appliedExceptions: [],
    };
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(DRILL.psi, psiCond)));
    window.dispatchEvent(new CustomEvent("plannel:mpPsiSearch", { detail: psiCond }));
    reduxDispatch(reduxUtil.updateHistoryState(DRILL.psi));
  };

  // ③ 능력 부하 → 자원 부하 분석. 카드에서 고른 자원을 그대로 이관(AutocompleteSearch 는 resCd 로 매칭, name 으로 표시)
  const openResourceUtil = () => {
    const sel = (payload.resources ?? []).find((r) => String(r.value) === String(resourceCd));
    return drillTo(DRILL.resourceUtil, {
      filter: { locations: drillLocations() },
      resource: sel ? { resCd: sel.value, name: sel.label } : null,
      currentPage: 1,
    });
  };

  // ④ 제약 원인 / ⑥ 예외 → 생산 예외 관리. ⚠️ 대상에 예외·원인 **유형 필터가 없다** → 거점만 넘긴다
  const openExceptions = () => drillTo(DRILL.exceptions, {
    filter: { locations: drillLocations(), category: null },
  });
  const openMaterialConstraints = () => drillTo(DRILL.materialConst, { filter: { locations: drillLocations() } });
  const openMpDemand = () => drillTo(DRILL.mpDemand, { filter: { locations: drillLocations() } });

  /*
   * ⑤ 부족 상위 N(제목) → 주문 추적. KPI 충족률·부족 금액도 같은 대상.
   *   orderStatus = 대상의 주문 상태 필터(다중 선택 배열). 부족 금액 KPI 는 SHORT 만 걸어 보낸다 —
   *   그 KPI 가 세는 대상(미출하 주문)과 대상 화면이 보여주는 행이 정확히 같아진다.
   *   ⚠️ 인자 기본값이 있으므로 **핸들러를 그대로 onClick 에 넘기지 말 것**(MouseEvent 가 상태로 들어간다).
   */
  const openOrderTracking = (orderStatus = []) => drillTo(DRILL.orderTracking, {
    filter: { locations: drillLocations() },
    period: drillPeriod(),
    mpOrderStatus: orderStatus,
    currentPage: 1,
  });

  /*
   * 권한 게이트 — 테넌트/사용자가 못 쓰는 화면으로 가는 드릴은 **링크 자체를 만들지 않는다**.
   *   onDrill/onClick 을 주지 않으면 카드 제목·KPI 카드가 평문으로 돌아간다(어포던스도 같이 사라진다).
   */
  const drillIf = (target, fn) => (isPermitted(target) ? fn : undefined);

  /*
   * 제약 원인별 대상 — 원인마다 손볼 화면이 다르다. 매핑에 **없는** 원인(other)만 예외 관리로 보낸다.
   *   ⚠️ 권한 없는 대상을 예외 관리로 흘리면 안 된다 — 자재 제약을 눌렀는데 엉뚱한 화면이 열린다.
   *   그래서 "알려진 원인인가"와 "그 대상이 열리는가"를 따로 본다: 알려졌는데 권한이 없으면 아무 일도 없다.
   */
  const constraintDrill = {
    material: drillIf(DRILL.materialConst, openMaterialConstraints),
    capacity: drillIf(DRILL.resourceUtil, openResourceUtil),
    data: drillIf(DRILL.mpDemand, openMpDemand),
  };
  const constraintFallback = drillIf(DRILL.exceptions, openExceptions);
  const openConstraintCause = (key) => {
    const fn = key in constraintDrill ? constraintDrill[key] : constraintFallback;
    fn?.();
  };
  const hasConstraintDrill = !!constraintFallback || Object.values(constraintDrill).some(Boolean);

  /*
   * ⑤ 부족 행 → 그 품목의 PSI.
   * ⚠️ shortageRows 는 거점을 **이름(loc)만** 준다(envelope §7) → 행의 거점은 못 넘긴다.
   *    상단 거점 필터만 함께 전달한다. 행 단위 거점까지 넘기려면 백엔드가 locCd 를 실어야 한다.
   */
  const openShortageRow = (row) => openPsi(row?.itemCd);

  // 카드 key → 드릴 핸들러 + 대상 화면 이름(툴팁). 카드가 추가되면 여기 등록한다.
  const drillDefs = {
    balance: { onDrill: drillIf(DRILL.planReview, openPlanReview), drillTitle: t("menuMpReview") },
    invProjection: { onDrill: drillIf(DRILL.psi, () => openPsi()), drillTitle: t("menuMpPsiSimulation") },
    capacity: { onDrill: drillIf(DRILL.resourceUtil, openResourceUtil), drillTitle: t("menuMpResourceUtilization") },
    constraint: { onDrill: drillIf(DRILL.exceptions, openExceptions), drillTitle: t("menuMpProductionException") },
    // 부족 상위 N → 주문 추적(부족 상태만). KPI 부족 금액과 같은 대상·같은 필터라 숫자가 어긋나지 않는다
    shortage: { onDrill: drillIf(DRILL.orderTracking, () => openOrderTracking([CodeUtil.orderStatusCode.SHORT])),
      drillTitle: t("menuMpOrderTracking") },
    exception: { onDrill: drillIf(DRILL.exceptions, openExceptions), drillTitle: t("menuMpProductionException") },
  };

  // 카드 key → 렌더 함수. drill = { onDrill, drillTitle, onDrillHover } → 카드 제목이 링크가 된다.
  const renderCard = (key, drill) => {
    switch (key) {
      case "balance":
        return <BalanceCard title={cardLabels.balance}
          data={payload.balance?.[balanceBucket] ?? payload.balance?.month ?? []}
          bucket={balanceBucket} weekAvailable={weekAvailable} onChangeBucket={setBalanceBucket}
          chartConfig={chartConfig} onOpenConfig={() => setConfigScope("balance")} onToggleSeries={toggleSeries}
          drill={drill} />;
      case "invProjection":
        return <InvProjectionCard title={cardLabels.invProjection}
          data={payload.invProjection?.[invBucket] ?? payload.invProjection?.month ?? []}
          bucket={invBucket} weekAvailable={weekAvailable} onChangeBucket={setInvBucket}
          chartConfig={chartConfig} onOpenConfig={() => setConfigScope("invProjection")} onToggleSeries={toggleSeries}
          drill={drill} />;
      case "capacity":
        return <CapacityCard title={cardLabels.capacity}
          data={payload.capacity?.[capacityBucket] ?? payload.capacity?.month ?? []}
          bucket={capacityBucket} weekAvailable={weekAvailable} onChangeBucket={setCapacityBucket}
          resources={payload.resources ?? []} resourceCd={resourceCd} onChangeResource={setResourceCd}
          chartConfig={chartConfig} onOpenConfig={() => setConfigScope("capacity")} onToggleSeries={toggleSeries}
          drill={drill} />;
      case "constraint":
        return <ConstraintCard title={cardLabels.constraint} data={payload.constraint ?? []}
          drill={drill} onSegmentDrill={hasConstraintDrill ? openConstraintCause : undefined} />;
      case "shortage":
        // 그룹 행에는 품목코드가 없다 → 롤업 중에는 행 드릴을 끈다(심을 품목이 없으면 대상 화면이 전체로 열린다).
        return <ShortageGrid title={cardLabels.shortage} rows={shownShortage} currencyCd={payload.currencyCd}
          topN={topN} onChangeTopN={saveTopN} drill={drill}
          onRowDrill={payload.itemGrouped ? undefined : drillIf(DRILL.psi, openShortageRow)}
          grouped={!!payload.itemGrouped} groupLabel={itemGroupLabel} />;
      case "exception":
        return <ExceptionCard title={cardLabels.exception} rows={payload.exceptionRows ?? []} drill={drill} />;
      case "lineProd":
        return <LineProdCard title={cardLabels.lineProd} rows={payload.lineProd ?? []} drill={drill} />;
      default:
        return null;
    }
  };

  const spanOf = (key) => CARD_DEFS.find((c) => c.key === key)?.span ?? 3;
  const minHOf = (key) => CARD_DEFS.find((c) => c.key === key)?.minH ?? 260;
  const visibleOrder = (layout?.order ?? DEFAULT_LAYOUT.order).filter((k) => !layout?.hidden?.[k]);

  // 6열 기준으로 카드를 실제 행으로 묶는다(span 누적, 6 초과 시 다음 행).
  const gridRows = visibleOrder.reduce((rows, key) => {
    const s = spanOf(key);
    const last = rows[rows.length - 1];
    if (!last || last.used + s > 6) rows.push({ keys: [key], used: s });
    else { last.keys.push(key); last.used += s; }
    return rows;
  }, []);
  // 각 행 = minmax(최소높이, 최소높이fr): 세로 여백이 남으면 fr 가 비례로 채워 흰 여백 제거,
  // 모자라면 하한으로 스크롤.
  const gridTemplateRows = gridRows
    .map((r) => { const m = Math.max(...r.keys.map(minHOf)); return `minmax(${m}px, ${m}fr)`; })
    .join(" ");

  return (
    <DashboardPage>
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        <CompactSelect displayEmpty value={locId} options={locOptions} onChange={setLocId} minWidth={112} />
        {levelOptions.length > 1 && (
          <CompactSelect displayEmpty value={itemLevel} options={[{ value: "", label: t("itemLevel") }, ...levelOptions]}
            onChange={setItemLevel} minWidth={96} />
        )}
        <IconButton size="small" onClick={() => setLayoutOpen(true)} sx={{ p: 0.4, color: "text.secondary" }}>
          <TuneIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </DashboardTopBar>

      {/* KPI 5장 — 공급계획이 수요를 얼마나 덮는가(충족률·부족) + 재고 여유(DOS) + 실행가능성(가동률) + 예외 */}
      {/* KPI 는 카드 안에 클릭 요소가 없다 → **카드 전체**가 드릴 링크(skill §9-1) */}
      <DashboardRow kpi columns={5}>
        <KpiCard label={t("fillRate")} value={`${kpis.fillRate?.value ?? 0}%`} deltaPct={kpis.fillRate?.deltaPct}
          sparkData={payload.spark?.fillRate} gradientId="sparkFillRate"
          onClick={drillIf(DRILL.orderTracking, () => openOrderTracking())} />
        {/* 부족 금액·예외 건수는 **줄어야 좋은** 지표 → deltaGood 을 직접 준다.
            안 주면 KpiCard 가 "올라가면 좋다"로 판단해 부족·예외가 늘어난 달이 초록 상승으로 표시된다. */}
        {/* 부족 금액 → 주문 추적을 **부족(SHORT) 상태만** 걸어서 연다 */}
        <KpiCard label={t("spShortAmt")} value={fmtMoney(kpis.shortage?.amt ?? 0)} deltaPct={kpis.shortage?.deltaPct}
          deltaGood={(kpis.shortage?.deltaPct ?? 0) <= 0}
          sub={`${t("shortQty")} ${fmtQty(kpis.shortage?.qty ?? 0)}`}
          sparkData={payload.spark?.shortage} gradientId="sparkShortage"
          onClick={drillIf(DRILL.orderTracking, () => openOrderTracking([CodeUtil.orderStatusCode.SHORT]))} />
        {/* DOS = 재고 ÷ 일평균 수요 → 분자·분모가 함께 보이는 PSI 로 보낸다(재고 잔량만 있는 화면은 절반만 설명한다) */}
        <KpiCard label={t("daysOfSupply")} value={`${kpis.dos?.value ?? 0}${daysUnit}`} deltaPct={kpis.dos?.deltaPct}
          sparkData={payload.spark?.dos} gradientId="sparkDos" onClick={drillIf(DRILL.psi, () => openPsi())} />
        <KpiCard label={t("capaUtil")} value={`${kpis.utilization?.value ?? 0}%`} deltaPct={kpis.utilization?.deltaPct}
          sparkData={payload.spark?.utilization} gradientId="sparkUtil"
          onClick={drillIf(DRILL.resourceUtil, openResourceUtil)} />
        <KpiCard label={t("spExceptionCount")} value={`${kpis.exception?.value ?? 0}`} deltaPct={kpis.exception?.deltaPct}
          deltaGood={(kpis.exception?.deltaPct ?? 0) <= 0}
          sparkData={payload.spark?.exception} gradientId="sparkException"
          onClick={drillIf(DRILL.exceptions, openExceptions)} />
      </DashboardRow>

      <Box sx={{
        flex: 1, minHeight: 0, overflow: "auto",
        display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows,
        gridAutoFlow: "row dense", gap: 1.5,   // 행 트랙(fr)이 남는 세로를 채워 하단 흰 여백 제거
      }}>
        {visibleOrder.map((key) => {
          // 카드 안에 클릭 요소(차트·범례·토글·기어·그리드)가 있으므로 클릭 영역은 **제목만**.
          //   제목 hover 를 받아 카드에 CARD_HOVER_SX 를 입혀 KPI 카드와 같은 하이라이트를 낸다.
          const def = drillDefs[key];
          const drill = def && { ...def, onDrillHover: (on) => setDrillHoverKey(on ? key : null) };
          return (
            <Card key={key}
              sx={{
                gridColumn: `span ${spanOf(key)}`, minHeight: 0,
                transition: CARD_HOVER_TRANSITION,
                ...(drillHoverKey === key && CARD_HOVER_SX),
              }}>
              {renderCard(key, drill)}
            </Card>
          );
        })}
      </Box>

      <DashboardLayoutModal
        open={layoutOpen}
        onClose={() => setLayoutOpen(false)}
        cards={CARD_DEFS.map((c) => ({ key: c.key, label: cardLabels[c.key] }))}
        layout={layout}
        onSave={(next) => { saveLayout(next); setLayoutOpen(false); }}
      />

      <ChartConfigModal
        open={configScope != null}
        onClose={() => setConfigScope(null)}
        config={chartConfig}
        onSave={(cfg) => { saveChartConfig(cfg); setConfigScope(null); }}
        measures={configMeasures}
        chartTypes={DW_CHART_TYPES}
      />
    </DashboardPage>
  );
};

export default SupplyPlanDashboard;
